import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Grid2, Typography } from "@mui/material";

import {
    statusColors,
    ProjectBaselineDescriptions,
    PreviousBaseline,
    ProjectManagerDto,
    DropdownOption
} from "../../interfaces/baselineInterface";
import { AdapterDayjs, AppLayout, CustomButton, CustomDropdown, DatePicker, dayjs, LocalizationProvider, SearchBar, useApi } from "../../utils/globalImports";

// ---------------------
// TYPE DEFINITIONS
// ---------------------


const PreviousBaselineList: React.FC = () => {
    const [baselines, setBaselines] = useState<PreviousBaseline[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const navigate = useNavigate();
    const { execute } = useApi();
    const [dropdownOptions, setDropdownOptions] = useState<{
        projectManagers: DropdownOption[];
    }>({
        projectManagers: [],
    });
    const [filters, setFilters] = useState({
        projectManagerId: "-1",
        month: "",
        year: "",
        projectName: ""
    });

    const filterFieldStyle = {
        width: 200,
        "& .MuiOutlinedInput-root": {
            height: 40,
            borderRadius: "8px",
        },
        "& .MuiInputBase-input": {
            padding: "8px 12px",
        },
        "& .MuiInputLabel-root": {
            top: "-4px"
        }
    };

    useEffect(() => {
        loadProjectManagers();
    }, []);

    const loadProjectManagers = async () => {
        const res = await execute<ProjectManagerDto[]>("get", "/ProjectBaseline/project-managers");

        const list = res.data ?? [];

        setDropdownOptions({
            projectManagers: list.map((p) => ({
                text: p.fullName,
                value: p.userID
            }))
        });
    };


    // Fetch data when component loads
    useEffect(() => {
        fetchPreviousBaselines();
    }, []);

    const fetchPreviousBaselines = async (updatedFilters = filters) => {
        setLoading(true);
        try {
            const query = new URLSearchParams({
                projectManagerId: updatedFilters.projectManagerId === "-1" ? "" : updatedFilters.projectManagerId,
                month: updatedFilters.month || "",
                year: updatedFilters.year || "",
                projectName: updatedFilters.projectName || ""
            }).toString();
            const url = `/ProjectBaseline/previous?${query}`;
            const res = await execute<PreviousBaseline[]>("get", url);
            setBaselines(res.data ? res.data : []);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching previous baselines:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field: string, value: any) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };
    // Handle View click
    const handleView = (baseline: PreviousBaseline) => {
        const baselineDate = new Date(baseline.baseLineDate);

        const monthName = baselineDate.toLocaleString("en-US", { month: "short" }); // "Nov"
        const year = baselineDate.getFullYear();
        navigate("/view-baseline", {
            state: {
                currentBaselineId: baseline.id,
                projectManagerID: baseline.projectManagerID,
                isPreviousBaseLine: true,
                baselineMonth: monthName,
                baselineYear: year
            },
        });
    };

    //if (loading) return <p>Loading previous baselines...</p>;
    // if (loading)
    //     return (
    //         <AppLayout>
    //             <Box
    //                 sx={{
    //                     display: "flex",
    //                     justifyContent: "center",
    //                     alignItems: "center",
    //                     height: "60vh"
    //                 }}
    //             >
    //                 <CircularProgress size={50} />
    //             </Box>
    //         </AppLayout>
    //     );

    return (
        <AppLayout>
            <div style={{display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0}}>
                <div>
                    <Typography
                        sx={{
                            fontFamily: "Poppins",
                            fontWeight: 500,
                            fontSize: "40px",
                            lineHeight: "60px",
                            color: "rgb(30, 102, 171)",
                            mb: 0,
                        }}
                    >
                        Previous Baselines
                    </Typography>
                    {/* <hr
                        style={{
                            backgroundColor: "#96c121",
                            height: "2px",
                            border: "none",
                            marginBottom: "20px",
                        }}
                    /> */}
                </div>
                <Box>
                    <Grid2
                        container
                        spacing={1.5}
                        alignItems="center"
                        sx={{
                            mb: 2,
                            flexWrap: "wrap",
                            gap: 1.5,
                            display: "flex",
                            flexDirection: "row",
                        }}
                    >
                        <Grid2>
                            <CustomDropdown
                                label="Select Project Manager"
                                value={filters.projectManagerId}
                                options={dropdownOptions.projectManagers}
                                onChange={(e) =>
                                    handleFilterChange("projectManagerId", Number(e.target.value))
                                }
                                sx={filterFieldStyle}   // fixed left-side width
                            />
                        </Grid2>

                        <Grid2>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Select Month & Year"
                                    views={["year", "month"]}
                                    value={
                                        filters.year && filters.month
                                            ? dayjs(`${filters.year}-${filters.month}-01`)
                                            : null
                                    }
                                    onChange={(date) => {
                                        handleFilterChange("month", date ? date.month() + 1 : "");
                                        handleFilterChange("year", date ? date.year() : "");
                                    }}
                                    sx={{
                                        "& .MuiInputBase-root": {
                                            height: "40px",
                                            borderRadius: "8px",
                                        },
                                        "& .MuiInputBase-input": {
                                            padding: "8px 12px",
                                        },
                                        "& .MuiInputLabel-root": {
                                            top: "-6px",          // moves label up
                                            transform: "translate(14px, 12px) scale(1)",   // keeps label centered
                                        },
                                        "& .MuiInputLabel-shrink": {
                                            transform: "translate(14px, -9px) scale(0.75) !important", // when focused
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        </Grid2>

                        {/* <Grid2>
                            <SearchBar
                                placeholder="Search Project"
                                onQueryChange={(value) => handleFilterChange("projectName", value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") fetchPreviousBaselines();
                                }}
                            />
                        </Grid2> */}
                        <Grid2>
                            <CustomButton
                                onClick={() => fetchPreviousBaselines(filters)}
                                backgroundColor="#1e66ab"
                                sx={{
                                    color: "#fff",
                                    px: 2,
                                    minWidth: 120,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Apply Filters
                            </CustomButton>
                        </Grid2>
                        <Grid2>
                            <CustomButton
                                onClick={() => {
                                    const reset = {
                                        projectManagerId: "-1",
                                        month: "",
                                        year: "",
                                        projectName: ""
                                    };

                                    setFilters(reset);
                                    fetchPreviousBaselines(reset);
                                }}
                                backgroundColor="#d32f2f"
                                sx={{
                                    color: "#fff",
                                    px: 2,
                                    minWidth: 120,
                                    height: 40,
                                    borderRadius: "8px",
                                }}
                            >
                                Clear Filters
                            </CustomButton>
                        </Grid2>
                    </Grid2>
                </Box>



                <Box
                    sx={{
                        width: "100%",
                        maxHeight: "65vh",      
                        overflow: "auto",        
                        borderRadius: "10px",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        mt: 2,
                        border: "1px solid #e0e0e0"
                    }}
                >
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "600px" }}>
                        <thead style={{ position: "sticky", top: 0, zIndex: 1 }}> {/* Keeps the header fixed while scrolling */}
                            <tr style={{ backgroundColor: "#1e66ab", color: "white", textAlign: "left" }}>
                                <th style={{ padding: "12px" }}>Project Manager</th>
                                <th style={{ padding: "12px" }}>Baseline Month</th>
                                <th style={{ padding: "12px" }}>Created Date</th>
                                <th style={{ padding: "12px" }}>Status</th>
                                <th style={{ padding: "12px" }}>No. of Projects</th>
                                <th style={{ padding: "12px" }}>Action</th>
                            </tr>
                        </thead>

                        <tbody>
                            {baselines.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={6}
                                        style={{
                                            padding: "20px",
                                            textAlign: "center",
                                            color: "#1e66ab",
                                            fontSize: "16px",
                                            height:"50vh"
                                        }}
                                    >
                                        No baseline found matching the search criteria.
                                    </td>
                                </tr>
                            ):(
                                baselines.map((b, index) => (
                                    <tr
                                        key={b.id}
                                        style={{
                                            backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff",
                                            transition: "0.2s",
                                            cursor: "pointer"
                                        }}
                                        onMouseEnter={(e) =>
                                            (e.currentTarget.style.backgroundColor = "#e8f3ff")
                                        }
                                        onMouseLeave={(e) =>
                                        (e.currentTarget.style.backgroundColor =
                                            index % 2 === 0 ? "#f9f9f9" : "#ffffff")
                                        }
                                    >
                                        <td style={{ padding: "12px" }}>{b.projectManagerName}</td>
                                        <td style={{ padding: "12px" }}>
                                            {new Date(b.baseLineDate)
                                                .toLocaleDateString("en-US", {
                                                    year: "numeric",
                                                    month: "short"
                                                })
                                                .replace(" ", "-")}
                                        </td>

                                        <td style={{ padding: "12px" }}>
                                            {new Date(b.createdDate).toLocaleDateString()}
                                        </td>

                                        <td style={{ padding: "12px" }}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    color: statusColors[b.status],
                                                    fontWeight: "600"
                                                }}
                                            >
                                                {ProjectBaselineDescriptions[b.status] ?? b.status}
                                            </Typography>
                                        </td>

                                        <td style={{ padding: "12px" }}>{b.noOfProjects}</td>

                                        <td style={{ padding: "12px" }}>
                                            <button
                                                style={{
                                                    padding: "6px 12px",
                                                    backgroundColor: "#1e66ab",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: "6px",
                                                    cursor: "pointer",
                                                    transition: "0.3s"
                                                }}
                                                onMouseEnter={(e) =>
                                                    (e.currentTarget.style.backgroundColor = "#155a96")
                                                }
                                                onMouseLeave={(e) =>
                                                    (e.currentTarget.style.backgroundColor = "#1e66ab")
                                                }
                                                onClick={() => handleView(b)}
                                            >
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </Box>
            </div>
        </AppLayout>
    );
};

export default PreviousBaselineList;